import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import ExportCsvButton from "../../components/ExportCsvButton";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

const SLOTS = [15, 30, 45, 60];

const blank = {
  full_name: "", email: "", password: "", phone: "",
  specialty: "", bio: "", photo_url: "",
  consultation_fee: 0, slot_duration_minutes: 30, is_active: true,
};

export default function Doctors() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const refresh = () =>
    api.get("/hospital/doctors").then((r) => setList(r.data)).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return list;
    return list.filter((d) =>
      [d.full_name, d.email, d.specialty].filter(Boolean).some((v) => v.toLowerCase().includes(k))
    );
  }, [list, q]);

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      full_name: d.full_name || "", email: d.email || "", password: "",
      phone: d.phone || "", specialty: d.specialty || "", bio: d.bio || "",
      photo_url: d.photo_url || "",
      consultation_fee: d.consultation_fee ?? 0,
      slot_duration_minutes: d.slot_duration_minutes ?? 30,
      is_active: !!d.is_active,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.email; // not editable
        if (!payload.password) delete payload.password;
        await api.patch(`/hospital/doctors/${editing.id}`, payload);
        toast.success("Doctor updated");
      } else {
        await api.post("/hospital/doctors", form);
        toast.success("Doctor created");
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (d) => {
    if (!window.confirm(`Deactivate ${d.full_name}?`)) return;
    try {
      await api.delete(`/hospital/doctors/${d.id}`);
      toast.success("Doctor deactivated");
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    }
  };

  return (
    <div data-testid="doctors-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ doctors</div>
          <h1 className="mt-2 text-3xl font-light tracking-tight">Your roster</h1>
          <p className="text-sm text-mb-fg2 mt-1">Create, edit & deactivate doctors at this hospital.</p>
        </div>
        <Button data-testid="add-doctor-button" onClick={openCreate}
          className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg h-10 px-5 font-medium">
          <Plus className="h-4 w-4 mr-2" /> New doctor
        </Button>
      </div>

      <div className="relative max-w-sm flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mb-fg2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="doctors-search"
            placeholder="Search by name, email, specialty"
            className="pl-9 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg focus-visible:ring-teal-500" />
        </div>
        <ExportCsvButton
          endpoint="/api/hospital/exports/doctors.csv"
          filename="doctors"
          params={{ include_inactive: true }}
          testid="hosp-doctors-export"
        />
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_0.7fr_0.6fr_auto] gap-4 px-6 py-3 border-b border-mb-border text-[10px] uppercase tracking-[0.25em] text-mb-fg2">
          <div>Doctor</div><div>Specialty</div><div>Email</div><div>Fee</div><div>Status</div><div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-center text-mb-fg2 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-10 text-center text-mb-fg2 text-sm" data-testid="doctors-empty">No doctors found.</div>
        ) : (
          <ul className="divide-y divide-mb-border">
            {filtered.map((d) => (
              <li key={d.id} data-testid={`doctor-row-${d.id}`}
                className="px-6 py-4 grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_0.7fr_0.6fr_auto] gap-2 md:gap-4 md:items-center">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-teal-500/10 text-teal-300 flex items-center justify-center text-sm font-medium shrink-0">
                    {(d.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{d.full_name}</div>
                    <div className="text-xs text-mb-fg2 truncate md:hidden">{d.email}</div>
                  </div>
                </div>
                <div className="text-sm text-mb-fg2 truncate">{d.specialty || "—"}</div>
                <div className="hidden md:block text-sm text-mb-fg2 truncate">{d.email}</div>
                <div className="text-sm text-mb-fg2">{formatCurrency(d.consultation_fee)}</div>
                <div>
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${
                    d.is_active ? "bg-teal-500/10 text-teal-300 border border-teal-500/20" : "bg-zinc-800 text-mb-fg2"
                  }`}>{d.is_active ? "active" : "inactive"}</span>
                </div>
                <div className="flex justify-start md:justify-end gap-2">
                  <Button onClick={() => openEdit(d)} data-testid={`edit-doctor-${d.id}`}
                    variant="ghost" className="h-9 px-3 hover:bg-mb-surface2">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button onClick={() => deactivate(d)} data-testid={`delete-doctor-${d.id}`}
                    disabled={!d.is_active}
                    variant="ghost" className="h-9 px-3 hover:bg-red-500/10 text-red-400 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-mb-surface border-mb-border text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-light text-2xl">{editing ? "Edit doctor" : "New doctor"}</DialogTitle>
            <DialogDescription className="text-mb-fg2 text-sm">
              {editing ? "Update fields and save. Leave password blank to keep the existing one." : "Create a doctor under this hospital. They can log in immediately."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
            <Field label="Full name" testid="form-full_name" value={form.full_name} required
              onChange={(v) => setForm({ ...form, full_name: v })} />
            <Field label="Email" type="email" testid="form-email" value={form.email} required disabled={!!editing}
              onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Phone" testid="form-phone" value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })} />
            <Field label={editing ? "New password (leave blank to keep)" : "Password"} type="password"
              testid="form-password" value={form.password} required={!editing}
              onChange={(v) => setForm({ ...form, password: v })} />
            <Field label="Specialty" testid="form-specialty" value={form.specialty} required
              onChange={(v) => setForm({ ...form, specialty: v })} />
            <Field label="Consultation fee (₹)" type="number" testid="form-fee" value={String(form.consultation_fee ?? 0)}
              onChange={(v) => setForm({ ...form, consultation_fee: parseFloat(v || "0") })} />
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Slot duration</Label>
              <Select value={String(form.slot_duration_minutes)}
                onValueChange={(v) => setForm({ ...form, slot_duration_minutes: parseInt(v, 10) })}>
                <SelectTrigger data-testid="form-slot" className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-mb-surface2 border-mb-border text-white">
                  {SLOTS.map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="Photo URL" testid="form-photo" value={form.photo_url}
              onChange={(v) => setForm({ ...form, photo_url: v })} />
            <div className="md:col-span-2 space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Bio</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                data-testid="form-bio" rows={3}
                className="bg-mb-surface2 border-mb-border text-white rounded-lg focus-visible:ring-teal-500" />
            </div>
            <div className="md:col-span-2 flex items-center justify-between rounded-lg border border-mb-border bg-mb-surface2 p-3">
              <div>
                <div className="text-sm">Active</div>
                <div className="text-xs text-mb-fg2">Inactive doctors are hidden from booking and cannot log in.</div>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="form-active" className="data-[state=checked]:bg-teal-500" />
            </div>
            <DialogFooter className="md:col-span-2 mt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="hover:bg-mb-surface2">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="form-submit"
                className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg font-medium">
                {saving ? "Saving…" : (editing ? "Save changes" : "Create doctor")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid, disabled }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid} disabled={disabled}
        className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg focus-visible:ring-teal-500 disabled:opacity-60" />
    </div>
  );
}
